import type { ReactNode } from 'react'
import type { ChartPane, ChartSeries } from '@/components/charts/time-chart'
import type { IndexDetail } from '@/lib/market/builders/indices'
import type { ChartRange } from '@/components/market/index-chart-card'

import { useCallback, useMemo, useState } from 'react'
import { dayTicks } from '@/components/charts/scales'
import { RANGE_OPTIONS, rangeStart } from '@/components/market/index-chart-card'
import { Segmented } from '@/components/market/segmented'
import { useLive } from '@/components/market/live-provider'
import { formatDay } from '@/lib/market/calendar'
import { formatNumber, formatPercent } from '@/lib/market/format'
import { indexQuoteAt } from '@/lib/market/live'
import Legend from '@/components/charts/legend'
import TimeChart from '@/components/charts/time-chart'

const COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)']
const RANGES = RANGE_OPTIONS.filter(option => option.value !== '1D')

const IndexComparison = ({ indices }: { indices: IndexDetail[] }): ReactNode => {
    const [range, setRange] = useState<ChartRange>('1Y')
    const { progress } = useLive()
    const days = indices[0]?.series.d ?? []

    const chart = useMemo(() => {
        const start = rangeStart(days, range)
        const visibleDays = days.slice(start)
        const series = indices.map((index, position) => {
            const values = index.series.c.slice(start)
            values[values.length - 1] = indexQuoteAt(index.live, progress).level
            const base = values[0]
            const rebased = values.map(value => (base > 0 ? (value / base) * 100 : null))

            return { index, color: COLORS[position % COLORS.length], rebased, change: base > 0 ? (values[values.length - 1] / base - 1) * 100 : 0 }
        })

        return { visibleDays, series }
    }, [indices, days, range, progress])

    const labels = useMemo(() => chart.visibleDays.map(day => formatDay(day, 'weekday') + ' ' + formatDay(day).slice(-4)), [chart.visibleDays])
    const xTicks = useCallback((width: number) => dayTicks(chart.visibleDays, width), [chart.visibleDays])

    const panes: ChartPane[] = useMemo(() => [{
        id: 'rebased',
        height: 280,
        format: value => formatNumber(value, 0),
        guides: [{ value: 100, label: 'Start of range = 100' }],
        series: chart.series.map(item => ({
            kind: 'line',
            id: item.index.code,
            label: item.index.short,
            color: item.color,
            values: item.rebased,
            format: (value: number) => formatNumber(value, 2)
        }) as ChartSeries)
    }], [chart.series])

    return (
        <div>
            <div className='mb-3 flex flex-wrap items-center justify-between gap-3'>
                <Legend
                    items={chart.series.map(item => ({
                        key: item.index.code,
                        label: item.index.short,
                        color: item.color,
                        value: formatPercent(item.change)
                    }))}
                />
                <Segmented label='Comparison range' options={RANGES} value={range} onChange={setRange} />
            </div>

            <TimeChart
                count={chart.visibleDays.length}
                labels={labels}
                xTicks={xTicks}
                panes={panes}
                ariaLabel='The three Kilele indices rebased to 100 at the start of the selected range'
            />
        </div>
    )
}

export default IndexComparison
